import { useEffect, useMemo, useRef } from 'react';

import { ConversationsV2ChatState } from '@spotify/confidence-graphql';

import { useChatEventExtensions, useToolExtensions } from '../../extensions';
import {
  findLastThoughtProcessIdx,
  groupChatEvents,
  groupThoughtProcess,
  useCachedChatEvents,
  useChat,
} from '../../hooks';
import { buildInlineBlocksIndex } from '../AssistantMessage/inlineBlocks';
import { LoadingIndicator } from '../LoadingIndicator';
import { ThoughtProcess } from '../ThoughtProcess';
import { ToolCallGroup } from '../ToolCallGroup';
import { ChatContextProvider } from './ChatContext';
import {
  DEFAULT_EVENT_POLL_INTERVAL_MS,
  RUNNING_EVENT_POLL_INTERVAL_MS,
} from './constants';

// Renders a child chat's transcript (thought processes, tool calls, assistant
// messages) as a read-only, self-scrolling list. Self-contained: it fetches the
// child chat's events and provides a child-scoped ChatContextProvider so inline
// block references resolve against the child transcript, letting it render anywhere
// — inside the in-thread subagent panel or a sidebar dialog — without the parent
// thread's providers.
export const ChildChatTranscriptView = ({
  childChatName,
  className,
}: {
  childChatName: string;
  className?: string;
}) => {
  const { chat } = useChat({ name: childChatName });
  const isChildRunning = chat?.state === ConversationsV2ChatState.Running;
  const { chatEvents } = useCachedChatEvents(childChatName, {
    pollInterval: isChildRunning
      ? RUNNING_EVENT_POLL_INTERVAL_MS
      : DEFAULT_EVENT_POLL_INTERVAL_MS,
  });
  const toolExtensions = useToolExtensions();
  const chatEventExtensions = useChatEventExtensions();
  const contentRef = useRef<HTMLDivElement>(null);
  const prevItemCountRef = useRef(0);

  const displayItems = useMemo(
    () => groupChatEvents(chatEvents ?? [], toolExtensions),
    [chatEvents, toolExtensions],
  );

  const processedItems = useMemo(
    () => groupThoughtProcess(displayItems, isChildRunning, toolExtensions),
    [displayItems, isChildRunning, toolExtensions],
  );

  const lastThoughtProcessIdx = useMemo(
    () => findLastThoughtProcessIdx(processedItems),
    [processedItems],
  );

  const chatContextValue = useMemo(
    () => ({
      chatName: childChatName,
      chatEvents: chatEvents ?? [],
      inlineBlocks: buildInlineBlocksIndex(chatEvents ?? []),
    }),
    [childChatName, chatEvents],
  );

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const prevCount = prevItemCountRef.current;
    const currentCount = processedItems.length;
    const threshold = 100;
    const isNearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    if (currentCount > prevCount && isNearBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    prevItemCountRef.current = currentCount;
  }, [processedItems.length]);

  return (
    <ChatContextProvider value={chatContextValue}>
      <div className={className} ref={contentRef}>
        {processedItems.map((item, idx) => {
          if (item.type === 'thought-process') {
            return (
              <ThoughtProcess
                key={item.key}
                items={item.items}
                isActive={isChildRunning && idx === processedItems.length - 1}
                isInterrupted={false}
                isLatest={idx === lastThoughtProcessIdx}
                defaultExpanded
              />
            );
          }

          if (item.type === 'tool-group') {
            return <ToolCallGroup key={item.key} entries={item.entries} />;
          }

          const extension = chatEventExtensions.find(ext =>
            ext.predicate(item.event),
          );

          if (!extension) {
            return null;
          }

          return <div key={item.key}>{extension.render(item.event)}</div>;
        })}
        {isChildRunning &&
          processedItems[processedItems.length - 1]?.type !==
            'thought-process' && <LoadingIndicator />}
      </div>
    </ChatContextProvider>
  );
};
